import { useFormik } from 'formik';
import { useEffect, useMemo } from 'react';
import { IoIosArrowForward } from 'react-icons/io';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button, Form, InputField, Loader, Modal } from '@/components';
import { DetailsPlayerSubmit } from '@/components/Team/DetailsPlayer';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import usePinsAccount from '@/hooks/usePinsAccount';
import { useTeamStore } from '@/hooks/useTeamStore';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import { postAssociatePin } from '@/services/pins';
import {
  TEXTS,
  alphanumericMaxLength,
  enterPinTeam11,
  validateFieldError,
  PACKS_BY_NAME,
} from '@/utils';

type Props = {
  onSubmitTransfer: (data: DetailsPlayerSubmit, pin: string) => void;
  showModal?: boolean;
};
const ModalAssociatePin = ({ onSubmitTransfer, showModal }: Props) => {
  const toast = useToast();
  const { isMobile, isDesktop } = useIsDesktop();
  const { handleSetPinsData, pinsData } = usePinsAccount();
  const { state } = useTeamStore();

  const isTransfer = useMemo(() => {
    return state.selectedPlayerOnList && state.selectedPlayerOnTeam;
  }, [state]);

  const { mutateAsync: postPin, isLoading } = useMutation(
    async (pin: string) => await postAssociatePin(pin),
    {
      onSuccess: (data) => {
        if (data.estado) {
          handleSetPinsData({
            showInputsModalPins: false,
            showModalWhatsPins: false,
            accountMoney: data.saldo || 0,
            isPremium: data.premium || false,
            pinsAvailable: data.pinesDisponibles || 0,
          });
          toast.show(TEXTS.pins.savedSuccessfully);
        } else {
          toast.show(data.mensaje);
        }
      },
      onError: (err) => {
        const error = err as Error;
        toast.showError(error);
      },
    }
  );

  const {
    touched,
    isValidating,
    isValid,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
    values,
    handleBlur,
    resetForm,
  } = useFormik({
    initialValues: {
      pin: '',
    },
    validateOnMount: false,
    validateOnBlur: true,
    validationSchema: enterPinTeam11,
    onSubmit: (val, { setSubmitting }) => {
      if (val) {
        if (!isTransfer) {
          postPin(val.pin);
        } else {
          if (state.selectedPlayerOnList?.id) {
            onSubmitTransfer(
              {
                player: state.selectedPlayerOnList,
                isCaptain: false,
              },
              val.pin
            );
          }
        }
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    resetForm();
    return () => {
      resetForm();
    };
  }, [resetForm, pinsData.showInputsModalPins]);

  const contentModalPin = useMemo(() => {
    return (
      <div className="lg:max-w-[246px] lg:m-auto">
        {isDesktop && (
          <button
            onClick={() => {
              handleSetPinsData({
                ...pinsData,
                showInputsModalPins: false,
                showModalWhatsPins: true,
              });
            }}
          >
            <IoIosArrowForward className="rotate-180 absolute h-8 w-8 text-gdt-gray top-3 left-5" />
          </button>
        )}
        <p className="text-sm font-medium mb-3 lg:text-center">
          Si tenés un pin, ingresalo acá.
        </p>
        <div className="lg:pb-0 mx-1">
          <Form.Form loading={isSubmitting} onSubmit={handleSubmit}>
            <Form.Field>
              <InputField
                autoComplete="off"
                behavior="default"
                error={validateFieldError(
                  touched.pin,
                  errors.pin,
                  values.pin.length
                )}
                maxLength={12}
                name="pin"
                placeholder="Pin"
                success={!errors.pin && values.pin.length > 0}
                type="text"
                value={values.pin}
                onBlur={handleBlur}
                onChange={(e) => {
                  if (alphanumericMaxLength(e.target.value, 12)) {
                    handleChange(e);
                  }
                }}
              />
            </Form.Field>
          </Form.Form>
          <Button
            className="w-full mt-8 h-12 lg:mt-6"
            disabled={!isValid || isValidating || !values.pin || isLoading}
            type="button"
            variant="primary"
            onClick={handleSubmit}
          >
            {isTransfer ? 'Confirmar transferencia' : 'Ingresar'}
          </Button>
          {isMobile && (
            <Button
              className="text-center font-bold mt-4 relative left-1/2 -translate-x-1/2"
              type="button"
              variant="text"
              onClick={() => {
                handleSetPinsData({
                  ...pinsData,
                  showInputsModalPins: false,
                  showModalWhatsPins: true,
                });
              }}
            >
              Volver
            </Button>
          )}
        </div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDesktop,
    isSubmitting,
    handleSubmit,
    touched.pin,
    errors.pin,
    values.pin,
    handleBlur,
    handleChange,
    isValid,
    isValidating,
    isLoading,
    isMobile,
    handleSetPinsData,
    pinsData,
    isTransfer,
  ]);
  return (
    <>
      {<Loader isVisible={isLoading} timeout={true} />}
      <Modal
        className="pt-7"
        controlledOnClose={() => {
          handleSetPinsData({
            ...pinsData,
            showInputsModalPins: false,
            showModalWhatsPins: false,
          });
        }}
        isControlledOpen={Boolean(
          pinsData.showInputsModalPins ||
            (showModal && !pinsData.showModalWhatsPins)
        )}
        render={() => {
          return contentModalPin;
        }}
        size="xs"
        variant="controlled"
        withBack={isDesktop}
      />
    </>
  );
};

export const ModalWhatsPins = ({ onSubmitTransfer, showModal }: Props) => {
  const { state } = useTeamStore();
  const { pinsData, handleSetPinsData } = usePinsAccount();
  const navigate = useNavigate();

  const handleCloseModal = () => {
    handleSetPinsData({
      ...pinsData,
      showInputsModalPins: false,
      showModalWhatsPins: false,
    });
  };

  const onClickGetPins = () => {
    handleCloseModal();
    navigate(ROUTES.myAccount);
  };

  const onClickGoPremium = () => {
    handleCloseModal();
    if (pinsData.isPremium) {
      navigate(ROUTES.myAccount);
    } else {
      navigate(ROUTES.myAccount, { state: PACKS_BY_NAME.PREMIUM });
    }
  };

  const isModalOpenedByTransfer =
    state.selectedPlayerOnList?.id && state.selectedPlayerOnTeam?.id;

  const contentModal = useMemo(() => {
    return (
      <>
        {isModalOpenedByTransfer ? (
          <>
            <h2 className="text-base font-bold text-center">
              Necesitás un pin
            </h2>
            <p className="mt-4 text-sm font-medium text-center m-auto max-w-[20rem] lg:max-w-[23rem]">
              Siendo DT Premium o ingresando un pin tenés transferencias
              ilimitadas en tu equipo hasta el comienzo de la fecha.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-base font-bold text-center">
              ¿Qué son los pines?
            </h2>
            <p className="mt-4 text-sm font-medium text-center m-auto max-w-[20rem] lg:max-w-[23rem]">
              Los pines son códigos que sirven para realizar{' '}
              <strong>transferencias</strong>. En Fútbol 5, siendo un DT Premium
              o con un pin tenés <strong>transferencias ilimitadas</strong> cada
              fecha y podés renovar tu equipo completo.
            </p>
          </>
        )}

        <div className="flex justify-between space-x-2 mt-8">
          <Button
            className="w-full px-2"
            type="button"
            variant="secondary"
            onClick={onClickGetPins}
          >
            Obtener Pin
          </Button>
          <Button
            className="w-full px-2"
            disabled={pinsData?.isPremium}
            type="button"
            variant="primary"
            onClick={onClickGoPremium}
          >
            Ser Premium
          </Button>
        </div>
        <Button
          className="my-7 lg:mt-7 lg:mb-0 text-center relative left-1/2 -translate-x-1/2 font-bold"
          type="button"
          variant="text"
          onClick={() => {
            handleSetPinsData({
              ...pinsData,
              showInputsModalPins: true,
              showModalWhatsPins: false,
            });
          }}
        >
          Si tenés un pin, ingresalo acá.
        </Button>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleSetPinsData,
    pinsData,
    state.selectedPlayerOnList?.id,
    state.selectedPlayerOnTeam?.id,
  ]);

  return (
    <>
      <Modal
        className="pt-12 pb-9"
        controlledOnClose={handleCloseModal}
        isControlledOpen={Boolean(
          pinsData.showModalWhatsPins ||
            (showModal && !pinsData.showInputsModalPins)
        )}
        render={() => {
          return contentModal;
        }}
        size="xs"
        variant="controlled"
      />

      {/* modal para ingreso de pin */}
      <ModalAssociatePin
        showModal={pinsData.showInputsModalPins}
        onSubmitTransfer={onSubmitTransfer}
      />
    </>
  );
};
